import { must } from './dom.ts';

/*
 * The slice of the Snap! IDE_Morph API the host pages drive, and the helper
 * that reaches into the embed iframe for it. Shared by the editor entry point
 * (snap.ts) and the read-only viewer (snap-view.ts).
 *
 * The embed page (static/snap-embed.html) exposes `snapReady` (a Promise) and
 * `snapIDE` once its window.onload has run. The read-only share viewer
 * (snap-view.ts) loads a project into this same full editor and enters
 * presentation mode, so the visitor gets Snap!'s own control bar (green flag /
 * pause / stop and the ⤢ toggle back to the editor) and can tinker — nothing is
 * wired to save, so their changes never reach the student's project.
 */
export interface SnapIDE {
  loadProjectXML(xml: string): void;
  // Lower-level open used by the viewer: the project loads over several
  // animation steps (Snap!'s nextSteps queue) and `callback` fires once it has
  // finished — the only reliable moment to (re-)enter presentation mode, since
  // opening a project forces edit mode partway through.
  openProjectString(xml: string, callback?: () => void): void;
  getProjectXML(): string;
  resetUnsavedChanges(): void;
  unsavedChanges(): boolean;
  runScripts(): void; // press the green flag
  toggleAppMode(appMode: boolean): void; // presentation (app) mode on/off
  // Stage size within the editor. false = the full-size (big) stage. The embed
  // auto-fits the stage to the iframe and the ratio only ever shrinks (never
  // grows back), so a project that laid out while the iframe was briefly small
  // stays stuck small; calling this with `false` once the frame is full-size
  // resets it to the big stage (bounded by what fits).
  toggleStageSize(isSmall: boolean): void;
  // Theme (plans/dark-mode.md): 'default' is Snap!'s dark look, 'bright' the
  // light one. refreshIDE() re-serializes and reopens the project — callers
  // must only use it when unsavedChanges() is false.
  setBrightTheme(): void;
  setDefaultTheme(): void;
  refreshIDE(): void;
}

type SnapEmbedWindow = Window & { snapReady?: Promise<SnapIDE>; snapIDE?: SnapIDE };

export const snapIDE = (frame: HTMLIFrameElement): Promise<SnapIDE> =>
  new Promise((resolve) => {
    const ready = () => {
      const w = frame.contentWindow as SnapEmbedWindow;
      Promise.resolve(w.snapReady ?? w.snapIDE).then((ide) =>
        resolve(must(ide, 'Snap IDE in embed frame')),
      );
    };
    if ((frame.contentWindow as SnapEmbedWindow | null)?.snapReady) {
      ready();
    } else {
      frame.addEventListener('load', ready, { once: true });
    }
  });
